import React, {
  forwardRef, useEffect, useImperativeHandle, useState,
} from 'react';
import {
  View, Text, StyleSheet, ActivityIndicator,
} from 'react-native';
import Style from '../../../../../../constants/Style';
import Colors from '../../../../../../constants/Colors';
import isMobile from '../../../../../../components/IsMobile';
import getTicket from '../../../../../../requests/Tickets/GetTicket';
import ActivityTabButton from './components/ActivityTabButton';
import TicketActivityListItem, { TicketActivityItem } from './components/TicketActivityItem';
import { ActivitySection, Ticket } from '../../../../../../constants/Types';

interface TicketActivityProps {
  selectedTicket: Ticket,
  users,
  sectionSelected?: ActivitySection,
  hideTabs?: boolean,
  // eslint-disable-next-line no-unused-vars
  setSectionSelected?: (tab: ActivitySection) => void,
}

const TicketActivity = forwardRef(({
  selectedTicket, users, sectionSelected, setSectionSelected, hideTabs = false,
}: TicketActivityProps, ref) => {
  const activityTabs = [ActivitySection.ALL, ActivitySection.REPORTER, ActivitySection.COMMENTS];
  const [loading, setLoading] = useState<boolean>(true);
  const [history, setHistory] = useState<TicketActivityItem[]>(null);
  const [comments, setComments] = useState<TicketActivityItem[]>(null);
  const [reporterMessages, setReporterMessages] = useState<TicketActivityItem[]>(null);
  const [activityItems, setActivityItems] = useState<TicketActivityItem[]>(null);

  useEffect(() => {
    setHistory(null);
    setComments(null);
    setReporterMessages(null);
    setActivityItems(null);
    refreshActivity();
  }, [selectedTicket]);

  const refreshActivity = () => {
    setLoading(true);
    getTicket(selectedTicket.id).then((res) => {
      if (res && !res.isError) {
        setComments(res.comments.filter((item: TicketActivityItem) => item.commentType !== 'REPORTER_COMMENT'));
        setHistory(res.history.filter((item: TicketActivityItem) => item.eventType !== 'MESSAGE_TO_REPORTER'));
        setReporterMessages([...res.history.filter((item: TicketActivityItem) => item.eventType === 'MESSAGE_TO_REPORTER'), ...res.comments.filter((item: TicketActivityItem) => item.commentType === 'REPORTER_COMMENT')]);
        setActivityItems([...res.history, ...res.comments]);
      }
    }).finally(() => setLoading(false));
  };

  useImperativeHandle(ref, () => ({
    refreshComments: (comment) => {
      const updatedComments = [...comments, comment];
      setComments(updatedComments);
      setActivityItems([...history, ...updatedComments]);
    },
    refreshActivity,
  }));

  return <View style={styles.container}>
    {!hideTabs && <View style={styles.header}>
      <View style={[{
        flexDirection: 'row', borderBottomColor: Colors.light.hereworksLightBlue200, borderBottomWidth: 1, flex: 1,
      }, isMobile && { flexWrap: 'wrap', marginStart: -8, gap: 5 }]}>
        {activityTabs.map((section: ActivitySection) => <ActivityTabButton
          key={`tab_button_${section}`}
          title={section}
          selected={sectionSelected === section}
          select={() => setSectionSelected?.(section)}
        />)}
      </View>
    </View>}
    <View style={styles.content}>
      <ActivityIndicator
        animating={loading}
        size='large'
        style={Style.activityIndicator}
      />
      {!loading && <>
        {sectionSelected === ActivitySection.ALL && (activityItems?.length > 0 ? activityItems
          .filter((item: TicketActivityItem) => item.text
            && item.createdOn).sort((a, b) => b.createdOn.localeCompare(a.createdOn))
          .map((item: TicketActivityItem, index: number) => <TicketActivityListItem
            key={`all_activity_item_${index}`}
            {...item}
            {...{ users }}
          />) : null)}
        {sectionSelected === ActivitySection.REPORTER && (reporterMessages?.length > 0
          ? reporterMessages
            .filter((item: TicketActivityItem) => item.text
              && item.createdOn).sort((a, b) => b.createdOn.localeCompare(a.createdOn))
            .map((item: TicketActivityItem, index: number) => <TicketActivityListItem
              key={`comment_item_${index}`}
              {...item}
              {...{ users }}
            />) : null)}
        {sectionSelected === ActivitySection.COMMENTS && (comments?.length > 0 ? comments
          .filter((item: TicketActivityItem) => item.text
            && item.createdOn).sort((a, b) => b.createdOn.localeCompare(a.createdOn))
          .map((item: TicketActivityItem, index: number) => <TicketActivityListItem
            key={`comment_item_${index}`}
            {...item}
            {...{ users }}
          />) : null)}
        {sectionSelected === ActivitySection.HISTORY && (history?.length > 0 ? history
          .filter((item) => item.text
            && item.createdOn).sort((a, b) => b.createdOn.localeCompare(a.createdOn))
          .map((item: TicketActivityItem, index: number) => <TicketActivityListItem
            key={`history_item_${index}`}
            {...item}
            {...{ users }}
          />) : <Text style={styles.noContentText}>
          {'No history to display'}
        </Text>)}
      </>}
    </View>
  </View>;
});

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  header: {
    marginTop: 24,
    marginBottom: 16,
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
  },
  content: {
    // minHeight: 100,
  },
  noContentText: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    alignSelf: 'center',
    marginVertical: '10%',
  },
});

export default TicketActivity;
